import { reactMount } from '@/lib/reactMount';
import { useEffect, useRef, useState } from 'react';
import { CALENDAR_PAGE_URL } from '@/Views/constants/constants';
import { useSignupForm } from './SignupBlock/hooks/useSignupForm.js';
import { SignupMediaPanel } from './SignupBlock/components/SignupMediaPanel';
import { SignupFormPanel } from './SignupBlock/components/SignupFormPanel';
import { ReferAFriend } from './SignupBlock/components/ReferAFriend';
import { useIsViewPortLessOrEqualTo } from '@/hooks/useIsViewPortLessOrEqualTo';
import { fireSignupStep } from '../../tracking/trackers/trackerComposite';

export function SignupBlock(props) {
  const isMobileTablet = useIsViewPortLessOrEqualTo(1279);
  const {
    umbraco,
    swiperRef,
    formRef,
    state,
    dispatch,
    passwordRules,
    handlePrevious,
    handleNext,
    handlePrev,
    handleOtherSoftwareChange,
    handleStartTrial,
    hasFieldError,
    getFieldError,
    getGeneralValidationErrors,
    referralId,
    showComponent,
    configError,
    formOptions,
  } = useSignupForm(props);

  const {
    bookingMethod,
    showRegionFields,
    category,
    teamSize,
    otherSoftware,
    apiError,
    formSuccess,
    password,
    hasTerms,
    marketingConsent,
    consentTemplateId,
    isMutating,
  } = state;

  const [activeIndex, setActiveIndex] = useState(0);
  const [showPromoCode, setShowPromoCode] = useState(false);
  const largestActiveIndexRef = useRef(-1);
  const eventQueueRef = useRef([]);
  const heapCheckIntervalRef = useRef(null);
  const showConsentFields = marketingConsent.doesCountryRequireConsent(
    state.countryCode,
  );

  // Function to track heap events, with queuing if heap is not available
  const trackHeapEvent = (eventName, properties) => {
    if (window.heap) {
      window.heap.track(eventName, properties);
    } else {
      // Queue the event if heap is not available
      eventQueueRef.current.push({ eventName, properties });
    }
  };

  // Function to process queued events
  const processEventQueue = () => {
    if (window.heap && eventQueueRef.current.length > 0) {
      eventQueueRef.current.forEach(({ eventName, properties }) => {
        window.heap.track(eventName, properties);
      });
      eventQueueRef.current = [];
    }
  };

  useEffect(() => {
    if (!showConsentFields) {
      dispatch({
        type: 'FIELD_CHANGE',
        field: 'marketingConsentGranted',
        value: false,
      });
    }
  }, [state.countryCode, marketingConsent]);

  // Check for heap availability periodically
  useEffect(() => {
    const checkHeapAvailability = () => {
      if (window.heap) {
        processEventQueue();
        if (heapCheckIntervalRef.current) {
          clearInterval(heapCheckIntervalRef.current);
          heapCheckIntervalRef.current = null;
        }
      }
    };

    // Check immediately
    checkHeapAvailability();

    // If not available, check every 100ms
    if (!window.heap) {
      heapCheckIntervalRef.current = setInterval(checkHeapAvailability, 100);
    }

    // Cleanup interval on unmount
    return () => {
      if (heapCheckIntervalRef.current) {
        clearInterval(heapCheckIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (activeIndex > largestActiveIndexRef.current && activeIndex <= 3) {
      fireSignupStep(activeIndex);

      largestActiveIndexRef.current = activeIndex;
    }
  }, [activeIndex]);

  const handleFieldChange = (event) => {
    let value = event.target.value;
    if (event.target.name === 'timeZone') {
      value = Number(value);
    }
    dispatch({ type: 'FIELD_CHANGE', field: event.target.name, value });
  };

  const handleRecaptchaToken = (token) => {
    dispatch({
      type: 'FIELD_CHANGE',
      field: 'gRecaptchaResponse',
      value: token,
    });
  };

  const handleCategorySelect = (selectedCategory) => {
    dispatch({
      type: 'FIELD_CHANGE',
      field: 'category',
      value: selectedCategory,
    });
    handleNext();
  };

  const handleTeamSizeSelect = (selectedSize) => {
    dispatch({ type: 'FIELD_CHANGE', field: 'teamSize', value: selectedSize });
    handleNext();
  };

  const handleBookingMethodSelect = (selectedMethod) => {
    dispatch({
      type: 'FIELD_CHANGE',
      field: 'bookingMethod',
      value: selectedMethod,
    });

    if (selectedMethod !== 'Other software') {
      dispatch({ type: 'FIELD_CHANGE', field: 'otherSoftware', value: '' });
      handleNext();
    }
  };

  const handleShowRegionFields = () => {
    dispatch({ type: 'FIELD_CHANGE', field: 'showRegionFields', value: true });
  };

  const handleShowPromoCode = () => {
    setShowPromoCode(true);
  };

  useEffect(() => {
    if (isMutating) {
      handleNext();
    }
    if (!isMutating && apiError) {
      handlePrev();
    }
  }, [isMutating]);

  useEffect(() => {
    if (!formSuccess) {
      return;
    }

    const redirectTimer = window.setTimeout(() => {
      window.location.href = state.loginUrl || CALENDAR_PAGE_URL;
    }, 1000);

    return () => {
      window.clearTimeout(redirectTimer);
    };
  }, [formSuccess, state.loginUrl]);

  // Show error state
  if (configError) {
    console.error('configError', configError);
    return null;
  }

  if (!showComponent) {
    return (
      <div className="max-w-screen-3xl min-h-[777px] xl:flex-row w-full mx-auto" />
    );
  }

  const isFullPageMode = !!umbraco.UseFullPageMode;

  const containerCss = isFullPageMode
    ? 'max-w-none min-h-[calc(100dvh-64px)] lg:h-[calc(100dvh-64px)] lg:overflow-hidden flex flex-col lg:flex-row w-full'
    : 'max-w-screen-3xl min-h-[777px] flex flex-col justify-center xl:flex-row w-full mx-auto';

  const mediaPanel = (
    <SignupMediaPanel
      mediaContent={umbraco}
      activeIndex={activeIndex}
      isFullPageMode={isFullPageMode}
    />
  );
  const formPanel = (
    <SignupFormPanel
      activeIndex={activeIndex}
      isMobileTablet={isMobileTablet}
      swiperRef={swiperRef}
      setActiveIndex={setActiveIndex}
      handlePrevious={handlePrevious}
      formOptions={formOptions}
      category={category}
      handleCategorySelect={handleCategorySelect}
      teamSize={teamSize}
      handleTeamSizeSelect={handleTeamSizeSelect}
      bookingMethod={bookingMethod}
      handleBookingMethodSelect={handleBookingMethodSelect}
      otherSoftware={otherSoftware}
      handleOtherSoftwareChange={handleOtherSoftwareChange}
      referralId={referralId}
      showPromoCode={showPromoCode}
      handleShowPromoCode={handleShowPromoCode}
      showRegionFields={showRegionFields}
      state={state}
      hasFieldError={hasFieldError}
      getFieldError={getFieldError}
      handleFieldChange={handleFieldChange}
      password={password}
      passwordRules={passwordRules}
      hasTerms={hasTerms}
      showConsentFields={showConsentFields}
      marketingConsent={marketingConsent}
      getGeneralValidationErrors={getGeneralValidationErrors}
      handleRecaptchaToken={handleRecaptchaToken}
      apiError={apiError}
      handleStartTrial={handleStartTrial}
      isMutating={isMutating}
      handleShowRegionFields={handleShowRegionFields}
      formRef={formRef}
      formSuccess={formSuccess}
      isFullPageMode={isFullPageMode}
    />
  );

  const referAFriendForm = formSuccess && (
    <ReferAFriend
      firstName={state.firstName}
      email={state.email}
      referralId={state.referralId}
    />
  );

  if (isFullPageMode) {
    return (
      <>
        <section id={umbraco?.AnchorTarget || null} className="bg-charcoal-50">
          <div className={containerCss}>
            {formPanel}
            {mediaPanel}
          </div>
        </section>

        {referAFriendForm}
      </>
    );
  } else {
    return (
      <>
        <section id={umbraco?.AnchorTarget || null} className="bg-charcoal-50">
          <div className={containerCss}>
            {mediaPanel}
            {formPanel}
          </div>
        </section>

        {referAFriendForm}
      </>
    );
  }
}

reactMount('SignupBlock', SignupBlock);
